import { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import moment from 'moment';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import DraggableCard from './DraggableCard';
import { Select, Avatar, Datepicker, MultiSelect } from 'src/components';
import UmbrellaProfile from 'src/assets/images/Umbrella-icon.svg';
import ProgressBar from './ProgressBar';
import DropdownLoadingSkeleton from './DropdownLoadingSkeleton';
import MemberSkeletonCard from './MemberSkeletonCard';
import { getColorForActivityLevel, StaffingStatusColors } from 'src/constants';
import FullStarIcon from 'src/assets/images/full-star-icon.svg';
import HalfStarIcon from 'src/assets/images/half-empty-star-icon.svg';
import EmptyStarIcon from 'src/assets/images/empty-star-icon.svg';

type MembersProps = {
  allMembers: any[];
  isMembersLoading: boolean;
  staffingStatus: any[];
  memberRolesFilters: any[];
  allMemberName: any[];
};

interface IFilters {
  staffingStatus: string | number;
  selectedRole: string | number;
  memberName: string;
  staffedUntil: string | Date;
  performances: string | number;
  memberNames: any[] | null;
}

const initialFilterObject: IFilters = {
  staffingStatus: '',
  selectedRole: '',
  memberName: '',
  staffedUntil: '',
  performances: '',
  memberNames: null,
};

const PERFORMANCE_DATA = [
  {
    label: 'All',
    value: 'all',
  },
  {
    label: '0 Rating',
    value: '0',
  },
  {
    label: '1 Rating',
    value: '1',
  },
  {
    label: '2 Rating',
    value: '2',
  },
  {
    label: '3 Rating',
    value: '3',
  },
  {
    label: '4 Rating',
    value: '4',
  },
  {
    label: '5 Rating',
    value: '5',
  },
];

const Members = ({
  allMembers,
  isMembersLoading,
  staffingStatus,
  memberRolesFilters,
  allMemberName,
}: MembersProps) => {
  const [membersData, setMembersData] = useState<any>(allMembers);
  const [filters, setFilters] = useState<IFilters>({ ...initialFilterObject });

  useEffect(() => {
    if (allMembers?.length) {
      setMembersData(allMembers);
    }
  }, [allMembers]);

  useEffect(() => {
    const { memberNames } = filters || {};
    if (memberNames !== null && memberNames?.length) {
      const searchLower = memberNames?.map((name: any) =>
        name?.value?.toLowerCase()
      );
      const results = allMembers?.filter((member) => {
        return searchLower.some(
          (term: any) => member?.name?.toLowerCase() === term
        );
      });
      setMembersData(results);
    }
  }, [filters, allMembers]);

  useEffect(() => {
    const {
      selectedRole,
      staffingStatus,
      staffedUntil,
      performances,
      memberNames,
    } = filters || {};
    if (memberNames === null) {
      let filteredData = [...allMembers];
      if (staffingStatus) {
        if (staffingStatus !== 'all') {
          filteredData = filteredData?.filter(
            (member) => member?.workloadStatus?.rank === staffingStatus
          );
        }
      }
      if (selectedRole && selectedRole !== 'all') {
        filteredData = filteredData?.filter(
          (member) => member?.role?.name === selectedRole
        );
      }
      if (staffedUntil && staffedUntil !== 'all') {
        const formatDate = moment(staffedUntil || '').format('MM-DD-YYYY');
        filteredData = filteredData?.filter(
          (member) => member?.untilDate === formatDate
        );
      }
      if (performances && performances !== 'all') {
        filteredData = filteredData?.filter(
          (member) =>
            Math.round(member?.rating) === parseInt(performances as string)
        );
      }
      setMembersData(filteredData);
    }
  }, [filters, allMembers]);

  const renderStars = (rating: number) => {
    if (rating === null || rating === 0) {
      return EmptyStarIcon;
    } else if (rating > 0 && rating <= 3) {
      return HalfStarIcon;
    } else {
      return FullStarIcon;
    }
  };
  const resetFilter = () => {
    setFilters(initialFilterObject);
  };

  return (
    <div className="font-SFProDisplay h-[87vh] xs:mr-6 bg-gray-50">
      <div className="pt-6 pl-6 pr-10 w-full bg-gray-50">
        <div className="flex items-center justify-between">
          <p className="text-blue-950 text-lg font-medium">Members List</p>
          <p
            className="text-indigo-600 text-sm font-medium cursor-pointer"
            onClick={resetFilter}
          >
            {isMembersLoading ? (
              <Skeleton height={18} width={100} />
            ) : (
              'RESET FILTERS'
            )}
          </p>
        </div>
        <div className="mt-4">
          {isMembersLoading ? (
            <Skeleton height={32} style={{ borderRadius: '20px' }} />
          ) : (
            <MultiSelect
              options={allMemberName}
              handleChange={(option) =>
                setFilters(() => ({
                  ...initialFilterObject,
                  memberNames: option,
                }))
              }
              value={filters.memberNames}
              error=""
              className="w-full min-h-9"
              placeholder="Search Member by Name"
            />
          )}
        </div>
        <div className="mt-4 flex justify-between items-center gap-3">
          <div className="w-full">
            {isMembersLoading ? (
              <DropdownLoadingSkeleton />
            ) : (
              <>
                <label className="block font-SFProDisplay font-medium  text-sm text-blue-950 mb-2">
                  Role:
                </label>
                <Select
                  options={memberRolesFilters}
                  value={filters.selectedRole}
                  className="w-full h-8"
                  optionClasses="text-sm text-blue-950 font-SFProDisplay font-medium "
                  placeholder="All"
                  onChange={(value: string | number) => {
                    if (value !== filters.selectedRole) {
                      setFilters((prevState) => ({
                        ...prevState,
                        memberNames: null,
                        selectedRole: value,
                      }));
                    }
                  }}
                />
              </>
            )}
          </div>
          <div className="w-full">
            {isMembersLoading ? (
              <DropdownLoadingSkeleton />
            ) : (
              <>
                <label className="block font-SFProDisplay font-medium  text-sm text-blue-950 mb-2 ">
                  Staffed Until:
                </label>
                <Datepicker
                  variant="input"
                  containerClass="w-full bg-white table-filter-date"
                  containerInnerClass="px-1 pt-[-40px] h-8 hover:border-borderBlue"
                  currentDate={filters.staffedUntil}
                  onChange={(date: Date) => {
                    setFilters((prevState) => ({
                      ...prevState,
                      memberNames: null,
                      staffedUntil: date,
                    }));
                  }}
                  showClearButton
                  handleClear={() => {
                    setFilters((prevState) => ({
                      ...prevState,
                      memberNames: null,
                      staffedUntil: '',
                    }));
                  }}
                />
              </>
            )}
          </div>
        </div>
        <div className="mt-4 sm:flex sm:justify-between items-center md:gap-3 xs:space-y-4">
          <div className="w-full">
            {isMembersLoading ? (
              <DropdownLoadingSkeleton />
            ) : (
              <>
                <label className="block font-SFProDisplay font-medium  text-sm text-blue-950 mb-2">
                  Experience:
                </label>
                <Select
                  options={[]}
                  value={''}
                  className="w-full h-8"
                  optionClasses="text-sm text-blue-950 font-SFProDisplay font-medium "
                  placeholder="All"
                />
              </>
            )}
          </div>
          <div className="w-full">
            {isMembersLoading ? (
              <DropdownLoadingSkeleton />
            ) : (
              <>
                <label className="block font-SFProDisplay font-medium  text-sm text-blue-950 mb-2">
                  Staffing Status:
                </label>
                <Select
                  options={staffingStatus}
                  value={filters.staffingStatus}
                  className="w-full h-8"
                  optionClasses="text-sm text-blue-950 font-SFProDisplay font-medium "
                  placeholder="All"
                  onChange={(value: string | number) => {
                    if (value !== filters.staffingStatus) {
                      setFilters((prevState) => ({
                        ...prevState,
                        memberNames: null,
                        staffingStatus: value,
                      }));
                    }
                  }}
                />
              </>
            )}
          </div>
          <div className="w-full">
            {isMembersLoading ? (
              <DropdownLoadingSkeleton />
            ) : (
              <>
                <label className="block font-SFProDisplay font-medium  text-sm text-blue-950 mb-2">
                  Performance:
                </label>
                <Select
                  options={PERFORMANCE_DATA}
                  value={filters.performances}
                  className="w-full h-8"
                  optionClasses="text-sm text-blue-950 font-SFProDisplay font-medium "
                  placeholder="All"
                  onChange={(value: string | number) => {
                    if (value !== filters.performances) {
                      setFilters((prevState) => ({
                        ...prevState,
                        memberNames: null,
                        performances: value,
                      }));
                    }
                  }}
                />
              </>
            )}
          </div>
        </div>
        <DndProvider backend={HTML5Backend}>
          <div className="mt-4 overflow-y-auto max-h-[50vh]">
            {isMembersLoading ? (
              Array.from({ length: 3 }).map((_, index) => (
                <MemberSkeletonCard key={index} />
              ))
            ) : membersData?.length ? (
              membersData?.map((member: any, index: number) => {
                const {
                  avatar,
                  name,
                  role,
                  workloadStatus,
                  untilDate,
                  calCulatedWorkLoad,
                  experience,
                  activeProjects,
                  rating,
                  capacity,
                } = member || {};
                console.log({ member });
                const getCapacityBackgroundColor =
                  getColorForActivityLevel(capacity);
                const roundOffRating = Math.round(rating);
                return (
                  <DraggableCard key={index} data={member}>
                    <div
                      key={index}
                      className="min-h-[111px] pl-4 pr-[26px] py-[15px] bg-white rounded-lg shadow flex justify-start mb-2 overflow-hidden"
                    >
                      <div className="max-w-[40px]">
                        <Avatar url={avatar} name={name} />
                      </div>
                      <div className=" w-full pl-2 mt-[-2px]">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2 mt-[-15px]">
                            <div className="text-blue-950 text-base font-medium capitalize overflow-hidden whitespace-nowrap text-ellipsis">
                              {name}
                            </div>
                            <div className="flex items-center gap-[2px]">
                              <img
                                src={renderStars(roundOffRating)}
                                className="max-w-3 max-h-[11.25px]"
                                alt="str icon"
                              />
                              <div className="text-slate-500 text-xs font-normal">
                                {roundOffRating}
                              </div>
                            </div>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs font-normal mb-[2px]">
                              Capacity:{' '}
                              <span
                                className="font-medium "
                                style={{ color: getCapacityBackgroundColor }}
                              >
                                {`${
                                  typeof capacity === 'number'
                                    ? capacity <= 100
                                      ? capacity
                                      : '100+'
                                    : '0' ?? '0'
                                }`}
                              </span>
                            </p>
                            <ProgressBar
                              key={calCulatedWorkLoad?.currentWeekAvgWorkLoad}
                              backgroundColor={getCapacityBackgroundColor}
                              percentage={capacity}
                            />
                          </div>
                        </div>
                        <div className="flex items-center text-slate-500 w-fit text-xs font-normal mt-[-10px]">
                          <span className="whitespace-nowrap">SE 2</span>
                          <div className="h-[9px] w-[1px] bg-[#ACB6D8] inline-block mx-[3px]"></div>
                          <span className="whitespace-nowrap">{`${experience?.years} yrs`}</span>
                          <div className="h-[9px] w-[1px] bg-[#ACB6D8] inline-block mx-[3px]"></div>
                          <img
                            src={UmbrellaProfile}
                            className="w-3 h-3 inline-block mr-[2px]"
                            alt="umbrella icon"
                          />
                          <span>0</span>
                          <div className="h-[9px] w-[1px] bg-[#ACB6D8] inline-block mx-[3px] "></div>
                          <span className="whitespace-nowrap overflow-hidden text-ellipsis">{`${role.rank}, ${role.name}`}</span>
                        </div>
                        <div>
                          {activeProjects?.length > 3 ? (
                            <div className=" whitespace-nowrap">
                              {activeProjects
                                ?.slice(0, 3)
                                ?.map((project: any, i: number) => (
                                  <p
                                    key={i}
                                    className=" px-1 text-blue-950 text-xs font-normal  rounded-md justify-start items-center mr-1 inline-flex bg-[#97A0AF] bg-opacity-10 capitalize"
                                  >
                                    {i + 1} {project?.projectCode}
                                  </p>
                                ))}
                              <p className="h-4 py-1 px-1 text-blue-950 text-xs font-normal  rounded-md justify-start items-center mr-1 inline-flex ">
                                {`+ ${activeProjects?.length - 3}`}
                              </p>
                            </div>
                          ) : (
                            activeProjects?.map((project: any, i: number) => (
                              <p
                                key={i}
                                className="h-4 py-1 px-1 text-blue-950 text-xs font-normal  rounded-md justify-start items-center mr-1 inline-flex bg-[#97A0AF] bg-opacity-10 capitalize"
                              >
                                {`${i + 1}.${project?.projectCode}`}
                              </p>
                            ))
                          )}
                        </div>
                        <div>
                          <div
                            style={{
                              color:
                                StaffingStatusColors[workloadStatus?.rank]
                                  ?.textColor ||
                                StaffingStatusColors['need_help'].textColor,
                              backgroundColor:
                                StaffingStatusColors[workloadStatus?.rank]
                                  ?.backgroundColor ||
                                StaffingStatusColors['need_help']
                                  .backgroundColor,
                            }}
                            className="text-xs font-normal min-w-[63px] h-4 px-1 py-2  bg-opacity-20 rounded justify-center items-center mr-2 inline-flex"
                          >
                            {workloadStatus?.name || 'Not Assigned'}
                          </div>

                          <span className="text-slate-500 text-xs font-normal font-SFProDisplay">
                            Staffed Until:{' '}
                          </span>
                          <span className="text-blue-950 text-xs font-normal font-SFProDisplay">
                            {moment(untilDate).isValid()
                              ? moment(untilDate || '').format('MM/DD/YYYY')
                              : 'Not Staffed'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </DraggableCard>
                );
              })
            ) : (
              <div className="text-blue-950 text-sm font-medium flex justify-center">
                No Member Found
              </div>
            )}
          </div>
        </DndProvider>
      </div>
    </div>
  );
};

export default Members;
